import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import Sidebar from "@/components/layout/Sidebar";
import Header from "@/components/layout/Header";
import BottomNav from "@/components/layout/BottomNav";
import Dashboard from "@/pages/Dashboard";
import Agents from "@/pages/Agents";
import Tasks from "@/pages/Tasks";
import Analytics from "@/pages/Analytics";
import Leaderboard from "@/pages/Leaderboard";
import AuditLog from "@/pages/AuditLog";
import SkillMarketplace from "@/pages/SkillMarketplace";
import SettingsPage from "@/pages/Settings";
import DigitalTwin from "@/pages/DigitalTwin";
import Command from "@/pages/Command";
import Departments from "@/pages/Departments";
import DepartmentDetail from "@/pages/DepartmentDetail";
import Atlas from "@/pages/Atlas";
import AgentDetail from "@/pages/AgentDetail";
import Approvals from "@/pages/Approvals";
import Constellation from "@/pages/Constellation";
import Connections from "@/pages/Connections";
import Workflows from "@/pages/Workflows";
import BuildMap from "@/pages/BuildMap";
import Academy from "@/pages/Academy";
import AiTeam from "@/pages/AiTeam";
import { GuideProvider } from "@/lib/guide";
import { GuideDrawer } from "@/components/guide/GuideDrawer";
import { Tour } from "@/components/guide/Tour";
import { Koko } from "@/components/koko/Koko";
import YourMap from "@/pages/YourMap";
import WorkflowBuilder from "@/pages/WorkflowBuilder";
import Workspaces from "@/pages/Workspaces";
import Audit from "@/pages/Audit";
import Reports, { SharedUsageReport } from "@/pages/Reports";
import AuditReport from "@/pages/AuditReport";
import Login from "@/pages/auth/Login";
import { ForgotPassword, ResetPassword } from "@/pages/auth/PasswordReset";
import { AcceptInvite, RequestAccess } from "@/pages/auth/Invite";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";

function Shell({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  return (
    <GuideProvider>
    <div className="flex h-screen overflow-hidden" data-testid="app-layout">
      <Sidebar open={sidebarOpen} onToggle={() => setSidebarOpen((s) => !s)} />
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <Header onMenuToggle={() => setSidebarOpen((s) => !s)} />
        <main className="flex-1 overflow-auto p-3 sm:p-4 md:p-6 lg:p-8 pb-20 lg:pb-8">{children}</main>
      </div>
      <BottomNav />
      <GuideDrawer />
      <Tour />
      <Koko />
    </div>
    </GuideProvider>
  );
}

const Loading = () => <div className="h-screen grid place-items-center font-mono text-xs text-muted-foreground" data-testid="auth-loading">Opening the house…</div>;

function Protected() {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  return <Shell><Outlet /></Shell>;
}

function Flexible({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  return user ? <Shell>{children}</Shell> : children;
}

function App() {
  useEffect(() => { document.documentElement.classList.add("dark"); }, []);
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/invite/:token" element={<AcceptInvite />} />
          <Route path="/request-access" element={<RequestAccess />} />
          <Route path="/audit" element={<Flexible><Audit /></Flexible>} />
          <Route path="/report/:id" element={<Flexible><AuditReport /></Flexible>} />
          <Route path="/usage/:token" element={<SharedUsageReport />} />
          <Route element={<Protected />}>
            <Route path="/" element={<Command />} />
            {/* New consolidated routes (Oct 2026 nav spec) */}
            <Route path="/setup" element={<BuildMap />} />
            <Route path="/ai-team" element={<AiTeam />} />
            <Route path="/ai-team/:slug" element={<AgentDetail />} />
            <Route path="/runs" element={<Tasks />} />
            <Route path="/results" element={<Reports />} />
            {/* Legacy routes preserved as deep-links / redirects would come later */}
            <Route path="/departments" element={<Departments />} />
            <Route path="/departments/:slug" element={<DepartmentDetail />} />
            <Route path="/map" element={<Constellation />} />
            <Route path="/map/:dept" element={<Constellation />} />
            <Route path="/atlas" element={<Atlas />} />
            <Route path="/atlas/:slug" element={<AgentDetail />} />
            <Route path="/approvals" element={<Approvals />} />
            <Route path="/workflows" element={<Workflows />} />
            <Route path="/build" element={<BuildMap />} />
            <Route path="/academy" element={<Academy />} />
            <Route path="/your-map" element={<YourMap />} />
            <Route path="/workflows/:id" element={<WorkflowBuilder />} />
            <Route path="/connections" element={<Connections />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/workspaces" element={<Workspaces />} />
            <Route path="/fleet" element={<Agents />} />
            <Route path="/tasks" element={<Tasks />} />
            <Route path="/analytics" element={<Analytics />} />
            <Route path="/audit-log" element={<AuditLog />} />
            <Route path="/settings" element={<SettingsPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster theme="dark" position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
